import { useCallback, useContext, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatHasAccessContext } from '../Chat/context';
import { useLiveAvatarContextOptional } from '../LiveAvatar/LiveAvatar.context';
import { useAvatarPanel } from './AvatarPanel.context';

export const AvatarControlBar = () => {
  const { speechRecognition, onClickMicrophone, onClickSubmit, textAreaRef } =
    useContext(InputControlsContext);
  const hasAccess = useContext(ChatHasAccessContext);
  const { expanded, setInteractionMode, setExpanded } = useAvatarPanel();
  const avatarCtx = useLiveAvatarContextOptional();

  // Mirrors the mic button in the chat input: true while STT is actively
  // capturing speech. When listening, the button becomes a stop/submit icon.
  const isListening = speechRecognition.micState === 'listening';

  // Avatar mute state — falls back to unmuted if no session context yet.
  const isMuted = avatarCtx?.isMuted ?? false;
  const toggleMute = avatarCtx?.toggleMute;

  // Whether the avatar is currently speaking — used to enable/disable interrupt.
  const isAvatarTalking = avatarCtx?.isAvatarTalking ?? false;

  const handleInterrupt = () => {
    // Cancel all queued paceRepeatAudio segments before interrupting so the
    // avatar doesn't resume speaking after the SDK stops the current segment.
    if (avatarCtx?.pendingAudioTimeoutsRef) {
      avatarCtx.pendingAudioTimeoutsRef.current.forEach(clearTimeout);
      avatarCtx.pendingAudioTimeoutsRef.current = [];
    }
    avatarCtx?.sessionRef.current?.interrupt();
  };

  // Tracks the micState value from the previous render so the effect below can
  // detect the specific 'listening' → 'inactive' transition.
  const prevMicStateRef = useRef(speechRecognition.micState);

  // Set to true when the user manually clicks the checkmark stop button.
  // Tells the auto-submit effect to skip firing (onClickSubmit already ran).
  const manualStopRef = useRef(false);

  // Starts STT — same as clicking the mic in the chat input.
  const handleMicStart = useCallback(() => {
    onClickMicrophone();
  }, [onClickMicrophone]);

  // Stops STT AND submits the current transcript. Marks the stop as manual so
  // the auto-submit effect below doesn't fire a duplicate submission.
  const handleMicStop = useCallback(() => {
    manualStopRef.current = true;
    // onClickSubmit internally stops the mic if it's still running, then fires
    // handleSubmit — so one call handles both stop and submit.
    onClickSubmit();
  }, [onClickSubmit]);

  // Auto-submit after silence: when the 5-second silence timeout causes STT to
  // stop on its own (micState: 'listening' → 'inactive', no manual stop), we
  // submit — but only if the textarea has non-empty content.
  useEffect(() => {
    const prevState = prevMicStateRef.current;
    prevMicStateRef.current = speechRecognition.micState;

    // Only act on the 'listening' to 'inactive' transition.
    if (prevState !== 'listening' || speechRecognition.micState !== 'inactive') {
      return;
    }

    if (manualStopRef.current) {
      // Manual stop: checkmark already called onClickSubmit. Just reset the flag.
      manualStopRef.current = false;
      return;
    }

    // Silence timeout fired: wait one tick for onSpeechFinished to populate the
    // textarea (it dispatches asynchronously after the 'end' event), then submit
    // only if the user actually said something and is allowed to submit.
    const timer = setTimeout(() => {
      const hasContent = (textAreaRef.current?.value ?? '').trim().length > 0;
      if (hasContent && hasAccess) {
        onClickSubmit();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [speechRecognition.micState, onClickSubmit, textAreaRef, hasAccess]);

  const handleExit = useCallback(() => {
    // Collapse the panel first so ChatPanel becomes visible again, then switch
    // to text mode. Both updates are batched into a single render pass by React.
    setExpanded(false);
    setInteractionMode('text');
  }, [setExpanded, setInteractionMode]);

  return (
    <Pill>
      {/* Mute/Unmute — silences the avatar video audio output */}
      <PillButton
        aria-label={isMuted ? 'Unmute avatar' : 'Mute avatar'}
        title={isMuted ? 'Unmute' : 'Mute'}
        onClick={toggleMute}
        disabled={toggleMute === undefined}
      >
        {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
      </PillButton>

      {/* Interrupt — stops the avatar mid-speech; disabled when not talking */}
      <PillButton
        aria-label={isAvatarTalking ? 'Stop avatar speaking' : 'Avatar not speaking'}
        title={isAvatarTalking ? 'Stop speaking' : 'Not speaking'}
        onClick={handleInterrupt}
        disabled={!isAvatarTalking}
      >
        {isAvatarTalking ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </PillButton>

      {/* Mic — only shown in expanded mode. Toggles speech-to-text (STT) exactly
          like the mic icon in the chat input. Does NOT change panel mode. */}
      {expanded &&
        (isListening ? (
          // STT is active: stops listening and submits the current transcript.
          <PillButton aria-label="Stop and submit" title="Stop and submit" onClick={handleMicStop}>
            <CheckCircleOutlineIcon fontSize="small" />
          </PillButton>
        ) : (
          // STT is off: start listening.
          <PillButton
            aria-label="Start voice input"
            title="Start voice input"
            onClick={handleMicStart}
            disabled={!hasAccess}
          >
            <MicIcon fontSize="small" />
          </PillButton>
        ))}

      {/* Exit — ends the avatar session and switches to Text mode */}
      <PillButton aria-label="Exit to text mode" title="Exit to text mode" onClick={handleExit}>
        <LogoutIcon fontSize="small" />
      </PillButton>
    </Pill>
  );
};

/* ─── Styled components ──────────────────────────────────────────────────── */

const Pill = styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;

  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  border-radius: 999px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
`;

/**
 * Generic pill icon button — transparent background with a subtle hover tint.
 * All control bar icons (Mute, Pause, Mic, ✓, Exit) share this same style.
 */
const PillButton = styled('button')`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: transparent;
  color: rgba(0, 0, 0, 0.65);

  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;

  &:hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;
